/**
 * Time utilities for converting UTC times (stored in DB) to the user's device timezone.
 * All times in the database are stored as UTC from the OpenF1 API.
 */

/**
 * Create a proper UTC Date object from date + time strings stored in the DB.
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @param {string} timeStr - Time in HH:MM format (UTC)
 * @returns {Date|null}
 */
export function utcToDate(dateStr, timeStr) {
  if (!dateStr || !timeStr || timeStr === 'TBD') return null;
  try {
    const d = new Date(`${dateStr}T${timeStr}:00Z`);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

/**
 * Format a UTC time in the user's device timezone.
 * @param {string} dateStr - Date in YYYY-MM-DD (UTC)
 * @param {string} timeStr - Time in HH:MM (UTC)
 * @returns {string} - Time like "14:30" in device timezone
 */
export const formatLocalTime = (dateStr, timeStr) => {
  const d = utcToDate(dateStr, timeStr);
  if (!d) return timeStr || 'TBD';
  return d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
};

/**
 * Format a UTC date in the user's device timezone (short form).
 * @returns {string} - Like "vr 8 mrt"
 */
export const formatLocalDate = (dateStr, timeStr) => {
  const d = utcToDate(dateStr, timeStr || '12:00');
  if (!d) return 'TBD';
  return d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });
};

/**
 * Format a UTC date in the user's device timezone (full form).
 * @returns {string} - Like "vrijdag 8 maart"
 */
export const formatLocalFullDate = (dateStr, timeStr) => {
  const d = utcToDate(dateStr, timeStr || '12:00');
  if (!d) return 'TBD';
  return d.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
};

/**
 * Check if a race's deadline (FP1 start) has passed.
 * @param {object} race - Race object with fp1.date and fp1.time
 * @returns {boolean}
 */
export const isDeadlinePassed = (race) => {
  if (!race?.fp1?.date) return false;
  const fp1 = utcToDate(race.fp1.date, race.fp1.time || '10:00');
  return fp1 ? new Date() >= fp1 : false;
};

/**
 * Given an array of races, find the next upcoming session.
 * Returns the soonest future session across all upcoming races.
 * @param {Array} races
 * @returns {{ race, sessionLabel, sessionDate: Date, deadlinePassed: boolean } | null}
 */
export const getNextSession = (races) => {
  if (!races?.length) return null;

  const now = new Date();
  let closest = null;

  const sessionDefs = [
    { key: 'fp1', label: 'VT1' },
    { key: 'fp2', label: 'VT2' },
    { key: 'fp3', label: 'VT3' },
    { key: 'sprint_qualifying', label: 'Sprint Kwalificatie' },
    { key: 'sprint', label: 'Sprint' },
    { key: 'qualifying', label: 'Kwalificatie' },
  ];

  for (const race of races) {
    if (race.status !== 'upcoming') continue;

    for (const s of sessionDefs) {
      const session = race[s.key];
      if (!session?.date || !session?.time) continue;
      const d = utcToDate(session.date, session.time);
      if (!d || d <= now) continue;
      if (!closest || d < closest.sessionDate) {
        closest = {
          race,
          sessionLabel: s.label,
          sessionDate: d,
          deadlinePassed: isDeadlinePassed(race),
        };
      }
    }

    // Also check the race session itself
    if (race.date && race.time) {
      const d = utcToDate(race.date, race.time);
      if (d && d > now) {
        if (!closest || d < closest.sessionDate) {
          closest = {
            race,
            sessionLabel: 'Race',
            sessionDate: d,
            deadlinePassed: isDeadlinePassed(race),
          };
        }
      }
    }
  }

  return closest;
};

// Keep backward-compatible exports for any code still using old names
export const convertToAmsterdamTime = formatLocalTime;
export const getAmsterdamDate = (dateStr, timeStr) => utcToDate(dateStr, timeStr || '12:00');
export const formatAmsterdamTimeWithZone = formatLocalTime;
